import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { use_slice_images, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { HOOK_GRID2D_CELL__button_size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__button_size';
import { show_toast_ai_description } from '@/utils/UTILS__generation';
import { assert_not_null_return } from '@shared/asserts/utils_asserts';
import { DTOImageAiDescription } from '@shared/dto/+image/image_ai_description/dto_image_ai_description';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ENUM__language_type } from '@shared/enums/ENUM__language_type';
import { useMemo, useState } from 'react';

interface Props {
  image: DTO__image_asset;
  entity: DTO__entity_with_grid2d;
  wrapper_class_name: string;
}

export function ButtonGrid2dImageImageToTextShow({ image, entity, wrapper_class_name }: Props) {
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { ai_descriptions_translation_in_progress, ai_descriptions_map } = use_slice_images(['ai_descriptions_translation_in_progress', 'ai_descriptions_map']);

  const grid2d_cell_button_size = HOOK_GRID2D_CELL__button_size(user_settings.ui_grid2d_cell_size);

  const [currentToastId, setCurrentToastId] = useState('');

  const ai_description_english = useMemo(() => get_ai_description_on_english(), [ai_descriptions_map]);
  const ai_description_translated = useMemo(() => get_ai_description_on_current_language(), [ai_descriptions_map, user_settings.regional_language]);

  const { dismiss, toast } = useToast();

  /*
   * EVENTS
   */

  async function onClick() {
    let target_description: DTOImageAiDescription;

    if (user_settings.regional_language == ENUM__language_type.ENGLISH || user_settings.regional_use_english_tokens_in_prompt) {
      target_description = assert_not_null_return(ai_description_english);
    } else {
      target_description = assert_not_null_return(ai_description_translated);
    }

    //close previous toast
    if (currentToastId) {
      dismiss(currentToastId);
      setCurrentToastId('');
    }

    await show_toast_ai_description(toast, target_description);
  }

  /*
   * METHODS
   */

  function get_ai_description_on_english() {
    return Array.from(ai_descriptions_map.values()).find(item => item.image_id == image._id && item.language == ENUM__language_type.ENGLISH);
  }

  function get_ai_description_on_current_language() {
    return Array.from(ai_descriptions_map.values()).find(item => item.image_id == image._id && item.language == user_settings.regional_language);
  }

  function is_disabled(): boolean {
    let out = ai_descriptions_translation_in_progress.has(image._id) || currentToastId != '';

    if (user_settings.regional_language == ENUM__language_type.ENGLISH || user_settings.regional_use_english_tokens_in_prompt) {
      out = out || !ai_description_english;
    } else {
      out = out || !ai_description_translated;
    }

    return out;
  }

  /*
   * RENDERS
   */

  function render_title(): string {
    if (ai_descriptions_translation_in_progress.has(image._id)) return 'ANIM'; //todo baz show animation

    return 'TXT';
  }

  /*
   * HTML
   */

  function render_button() {
    if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.IMAGE_GET_AI_DESCRIPTION)) return null; //action is not allowed for this user
    if (!ai_description_english) return null; //ai description not created
    //todo:check is user guest

    return (
      <div className={wrapper_class_name}>
        <UI_PRIMITIVE__button
          disabled={is_disabled()}
          variant="no-border-on-component"
          size="square-10"
          onClick={onClick}
          className={`cursor-pointer rounded-lg p-0 font-bold bg-bar text-bar-foreground 
        ${grid2d_cell_button_size}
        
        `}
        >
          {render_title()}
        </UI_PRIMITIVE__button>
      </div>
    );
  }

  return render_button();
}
